import { Button, ClassName, Tray } from '@busybusy/webapp-react-ui';
import { ColumnIcon } from 'assets/icons';
import classNames from 'classnames';
import { Header, IconButton } from 'components';
import { IActionHeaderTabData } from 'components/foundation/ActionHeaderTabBar/ActionHeaderTabBar';
import TabBar from 'components/foundation/TabBar/TabBar';
import ArrowedDateRange from 'components/foundation/ranges/ArrowedDateRange/ArrowedDateRange/ArrowedDateRange';
import { useQueryParams, useTimeRangeQueryParams } from 'hooks';
import useEquipmentInspectionsReportSettingsUpdate from 'hooks/models/member-settings/useEquipmentInspectionsReportSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import { t } from 'i18next';
import { DateTime } from 'luxon';
import TimeRange from 'types/TimeRange';
import TimeRangeType from 'types/TimeRangeType';
import { QueryParam } from 'utils/constants/queryParam';
import { utcToday } from 'utils/dateUtils';
import EquipmentInspectionReportActionBar from '../EquipmentInspectionReportActionBar/EquipmentInspectionReportActionBar';
import './EquipmentInspectionsReportTabHeader.scss';

export interface IEquipmentInspectionsReportTabHeaderProps {
  className?: ClassName;
  selectedTab: EquipmentInspectionsTabType;
  checkedItems: string[];
  timeRange?: TimeRange<DateTime> | null;
  onTabSelected: (tab: EquipmentInspectionsTabType) => void;
  handleDelete: (ids: string[]) => void;
  setCheckedItems: (ids: string[]) => void;
  handleColumnButtonClicked: () => void;
  onClickChecklists: () => void;
}

export enum EquipmentInspectionsTabType {
  UPCOMING = 'upcoming',
  COMPLETED = 'completed',
}

const EquipmentInspectionsReportTabHeader = (props: IEquipmentInspectionsReportTabHeaderProps) => {
  const {
    className,
    selectedTab,
    checkedItems,
    timeRange: parsedTimeRange,
    onTabSelected,
    handleDelete,
    setCheckedItems,
    handleColumnButtonClicked,
    onClickChecklists,
  } = props;

  const { getParam, updateParams } = useQueryParams();

  const memberSettings = useMemberSettings();
  const updateEquipmentInspectionsSettings = useEquipmentInspectionsReportSettingsUpdate();

  const timeRangeTypeParam = getParam(QueryParam.TIME_RANGE_TYPE) as TimeRangeType;
  const currentTimeRangeType =
    timeRangeTypeParam ??
    memberSettings?.web?.features?.equipmentInspectionsReport?.equipmentInspectionsCompletedReport
      ?.lastUsedTimeRangeType ??
    TimeRangeType.MONTHLY;

  const { forward, back, timeRange, forwardEnabled, backEnabled } = useTimeRangeQueryParams(
    currentTimeRangeType,
    parsedTimeRange,
    null,
    utcToday().endTime,
    'utc'
  );

  const handleTabSelection = (tab: EquipmentInspectionsTabType) => {
    updateParams({ [QueryParam.EQUIPMENT_INSPECTION_REPORT_TYPE]: tab });
    updateEquipmentInspectionsSettings([{ key: 'lastUsedTab', payload: tab }]);
    onTabSelected(tab);
  };

  const tabs: IActionHeaderTabData<EquipmentInspectionsTabType>[] = [
    { label: t('Complete'), displayValue: '', tabValue: EquipmentInspectionsTabType.COMPLETED },
    { label: t('Upcoming'), displayValue: '', tabValue: EquipmentInspectionsTabType.UPCOMING },
  ];

  const classes = classNames('equipment-inspections-report-tab-header', className);

  return (
    <Header
      className={classes}
      actionBar={
        <EquipmentInspectionReportActionBar
          checkedIds={checkedItems}
          onDelete={handleDelete}
          unselectAll={setCheckedItems}
        />
      }
    >
      <Tray spacing={1}>
        <TabBar tabs={tabs} selectedTab={selectedTab} onTabSelection={handleTabSelection} />
        {selectedTab === EquipmentInspectionsTabType.COMPLETED && (
          <>
            <ArrowedDateRange
              back={back}
              forward={forward}
              timeRange={timeRange}
              forwardEnabled={forwardEnabled}
              backEnabled={backEnabled}
              timeRangeType={currentTimeRangeType}
              className={'equipment-inspections-date-range'}
            />
          </>
        )}
      </Tray>
      <Tray align="right">
        <Button onClick={onClickChecklists} type="secondary">
          {t('Checklists')}
        </Button>
        <IconButton onClick={handleColumnButtonClicked} svg={ColumnIcon} />
      </Tray>
    </Header>
  );
};

export default EquipmentInspectionsReportTabHeader;
